import React from "react";
import { IResourceComponentsProps } from "@refinedev/core";
import { List, EditButton, DeleteButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import { ActionIcon, Badge, Flex, LoadingOverlay, Pagination, ScrollArea, Select, Table } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { OfferConfiguration } from "entities/cancel_flow";
import { selectBrands, colorsByBrand } from "entities/cancel_flow/brand";
import { selectSegments, colorsBySegment } from "entities/cancel_flow/segment";
import { selectOfferTypes, colorsByOfferType } from "entities/cancel_flow/offerType";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import useCancelFlowPermissions from "./useCancelFlowPermissions";

const activeOptions = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export const OfferConfigurationList: React.FC<IResourceComponentsProps> = () => {
  const { canCreate, canEdit, canDelete } = useCancelFlowPermissions();

  const columns = React.useMemo<ColumnDef<OfferConfiguration>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "brand",
        accessorKey: "brand",
        header: "Brand",
        cell: ({ getValue }) => {
          const value = getValue<OfferConfiguration["brand"]>();
          return (
            <Badge variant="filled" color={colorsByBrand[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<OfferConfiguration, any> }) => (
            <Select
              placeholder="Brand"
              data={selectBrands}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "segment",
        accessorKey: "segment",
        header: "Segment",
        cell: ({ getValue }) => {
          const value = getValue<OfferConfiguration["segment"]>();
          return (
            <Badge variant="filled" color={colorsBySegment[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<OfferConfiguration, any> }) => (
            <Select
              placeholder="Segment"
              data={selectSegments}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "offer_type",
        accessorKey: "offer_type",
        header: "Offer Type",
        cell: ({ getValue }) => {
          const value = getValue<OfferConfiguration["offer_type"]>();
          return (
            <Badge variant="filled" color={colorsByOfferType[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<OfferConfiguration, any> }) => (
            <Select
              placeholder="Offer Type"
              data={selectOfferTypes}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "offer_value",
        accessorKey: "offer_value",
        header: "Offer Value",
      },
      {
        id: "chargebee_coupon_id",
        accessorKey: "chargebee_coupon_id",
        header: "Coupon ID",
      },
      {
        id: "weight",
        accessorKey: "weight",
        header: "Weight",
      },
      {
        id: "is_active",
        accessorKey: "is_active",
        header: "Status",
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <Badge variant="filled" color={value ? "green" : "gray"}>
              {value ? "Active" : "Inactive"}
            </Badge>
          );
        },
        meta: {
          filterElement: ({ header }: { header: Header<OfferConfiguration, any> }) => (
            <Select
              placeholder="Status"
              data={activeOptions}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row: { original } }) {
          return (
            <Flex columnGap={4}>
              {canEdit && <EditButton hideText recordItemId={original.id} />}
              {canDelete && <DeleteButton hideText recordItemId={original.id} />}
            </Flex>
          );
        },
        meta: { isPlaceholder: true },
      },
    ],
    [canEdit, canDelete]
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { isFetching, refetch },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      pagination: { pageSize: 50 },
    },
  });

  return (
    <List
      canCreate={canCreate}
      headerButtons={({ defaultButtons }) => (
        <>
          <ActionIcon variant="subtle" onClick={() => refetch()} loading={isFetching}>
            <IconRefresh size={18} />
          </ActionIcon>
          {defaultButtons}
        </>
      )}
    >
      <AppliedFilters<OfferConfiguration> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<OfferConfiguration> headerGroups={getHeaderGroups()} />
          <Rows<OfferConfiguration> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
